<template>
    <div class="container">
        <div class="titleContainer">
            <div style="display: flex; flex-direction: column">
                <div style="display: flex; justify-content: center; align-items: center">
                    <div class="common-title">{{ "건축물에너지효율등급" }}</div>
                    <span
                        style="font-size: 14px; margin-left: 10px; cursor: pointer"
                        @click="() => $refs.efficiencyGradeModal.show()"
                    >
                        <i class="fa fa-question-circle"></i>
                    </span>
                </div>
                <small class="common-subtitle">
                    {{ `단위면적당 1차에너지 소요량 (${data.values.grade.unitInfo?.unit || "kWh/m²"} · ${timeLabel})` }}
                </small>
            </div>
        </div>
        <div ref="pyramidChart" class="pyramidChart"></div>

        <b-modal ref="efficiencyGradeModal" hide-footer @hidden="() => $refs.efficiencyGradeModal.hide()">
            <template #modal-header>
                <div class="d-flex justify-content-between align-items-center w-100">
                    <h5 class="mb-0">건축물 에너지 효율 등급 기준</h5>
                    <div class="d-flex justify-content-end" style="font-size: 14px">
                        <span class="setting-icon">
                            <i class="fas fa-times" @click="() => $refs.efficiencyGradeModal.hide()"></i>
                        </span>
                    </div>
                </div>
            </template>
            <template #default>
                <table class="table table-bordered table-striped text-center">
                    <thead class="thead-dark">
                        <tr>
                            <th scope="col">등급</th>
                            <th scope="col">1차 에너지 소요량 (kWh/㎡·yr) 이하</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(grade, index) in energyGrades" :key="`ENERGY-GRADE-${index}`">
                            <td>{{ grade.grade }}</td>
                            <td>{{ grade.maxConsumption }}</td>
                        </tr>
                    </tbody>
                </table>
            </template>
        </b-modal>
    </div>
</template>

<script>
import * as echarts from "echarts";

/** pill 열 중심 x (x축 0~100). ZebGradeChart PILL_SLOT_X와 동기화 */
const PILL_SLOT_X = { cert: 70, run: 82, totRun: 94 };
const PILL_SYMBOL_SIZE = [30, 20];
const X_AXIS_MAX = 100;
/** ZEB BAR_AREA_MAX(62)와 동일 — 트랙·피라미드 막대 최대 길이 */
const GRADE_TRACK_MAX = 62;
/** gradeData.value 설계 기준 최대(7등급 55) → GRADE_TRACK_MAX로 스케일 */
const GRADE_VALUE_REF_MAX = 55;
const CHART_PAD_TOP = 15;
const CHART_PAD_BOTTOM = 15;
/** 카테고리 밴드 대비 막대·간격 (픽셀 barWidth는 간격이 안 보일 수 있음) */
const BAR_CATEGORY_GAP = "20%";
const BAR_WIDTH = "90%";

const THEME = {
    cert: { main: "#5C6BC0", light: "#7986CB" },
    run: { main: "#43A047", light: "#66BB6A" },
    totRun: { main: "#1E88E5", light: "#42A5F5" },
};

export default {
    props: ["data", "timeLabel"],
    name: "PyramidBarChart",
    // components: { CommonTitlePanel },
    data() {
        return {
            chart: null,
            resizeHandler: null, // resize 핸들러 참조 저장
            gradeData: [
                { label: "1+++", value: 10 },
                { label: "1++", value: 15 },
                { label: "1+", value: 20 },
                { label: "1", value: 25 },
                { label: "2", value: 30 },
                { label: "3", value: 35 },
                { label: "4", value: 40 },
                { label: "5", value: 45 },
                { label: "6", value: 50 },
                { label: "7", value: 55 },
            ],
            gradeColors: {
                run: "#388E3C",
                totRun: "#1976D2",
                cert: "#546E7A",
            },
            energyGrades: [
                { grade: "1+++", maxConsumption: 80 },
                { grade: "1++", maxConsumption: 140 },
                { grade: "1+", maxConsumption: 200 },
                { grade: "1", maxConsumption: 260 },
                { grade: "2", maxConsumption: 320 },
                { grade: "3", maxConsumption: 380 },
                { grade: "4", maxConsumption: 450 },
                { grade: "5", maxConsumption: 520 },
                { grade: "6", maxConsumption: 610 },
                { grade: "7", maxConsumption: 700 },
            ],
        };
    },
    watch: {
        data() {
            this.$nextTick(() => this.renderChart());
        },
    },
    computed: {
        timeName() {
            const headers = this.data?.headers;
            if (!Array.isArray(headers) || !headers[0]?.timeDsvn) {
                return "당월";
            }
            return headers[0].timeDsvn === "MONTH" ? "당월" : "올해";
        },
        yLabels() {
            return this.gradeData.map((item) => item.label);
        },
    },
    mounted() {
        this.renderChart();
        this.resizeHandler = this.resizeChart.bind(this);
        window.addEventListener("resize", this.resizeHandler);
    },

    beforeDestroy() {
        this.cleanupChart();
    },

    destroyed() {
        // console.log(`[DESTROYED] EnergyEfficiencyChart`);
    },
    methods: {
        /* 차트 정리 메서드 */
        cleanupChart() {
            // 이벤트 리스너 제거
            if (this.resizeHandler) {
                window.removeEventListener("resize", this.resizeHandler);
                this.resizeHandler = null;
            }

            // 차트 인스턴스 정리
            if (this.chart) {
                this.chart.dispose();
                this.chart = null;
            }
        },

        resizeChart() {
            if (this.chart && !this.chart.isDisposed()) {
                this.chart.resize();
            }
        },
        pillGradient(themeKey) {
            const t = THEME[themeKey];
            return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: t.light },
                { offset: 1, color: t.main },
            ]);
        },
        lightenHex(hex, amount = 0.14) {
            const raw = hex.replace("#", "");
            const num = parseInt(raw, 16);
            const r = Math.min(255, ((num >> 16) & 0xff) + Math.round(255 * amount));
            const g = Math.min(255, ((num >> 8) & 0xff) + Math.round(255 * amount));
            const b = Math.min(255, (num & 0xff) + Math.round(255 * amount));
            return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
        },
        gradeBarGradient(baseColor) {
            return new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: this.lightenHex(baseColor) },
                { offset: 1, color: baseColor },
            ]);
        },
        gradeToYIndex(grade) {
            if (!grade) return -1;
            return this.yLabels.indexOf(grade);
        },
        scaleGradeBarValue(value) {
            return (value / GRADE_VALUE_REF_MAX) * GRADE_TRACK_MAX;
        },
        getActiveGradeIndexes(certifiedGrade, measuredGrade, totalOperatingGrade) {
            return [
                this.gradeToYIndex(certifiedGrade),
                this.gradeToYIndex(measuredGrade),
                this.gradeToYIndex(totalOperatingGrade),
            ].filter((idx) => idx >= 0);
        },
        buildGradeBarSeriesData(colorGradient, activeYIndexes) {
            return this.gradeData.map((item, index) => {
                const isActive = activeYIndexes.includes(index);
                const baseColor = colorGradient[index];
                return {
                    value: this.scaleGradeBarValue(item.value),
                    name: item.label,
                    itemStyle: {
                        color: this.gradeBarGradient(baseColor),
                        borderRadius: [0, 8, 8, 0],
                        shadowColor: isActive ? "rgba(30, 136, 229, 0.45)" : "rgba(13, 71, 161, 0.12)",
                        shadowBlur: isActive ? 10 : 4,
                        shadowOffsetY: isActive ? 2 : 1,
                        borderColor: isActive ? "rgba(255, 255, 255, 0.85)" : "rgba(255, 255, 255, 0.35)",
                        borderWidth: isActive ? 1.5 : 1,
                    },
                };
            });
        },
        buildMarkPointData(measuredGrade, certifiedGrade, totalOperatingGrade) {
            const timeName = this.timeName;
            const indicators = [
                {
                    key: "cert",
                    grade: certifiedGrade,
                    shortLabel: "인증",
                    fullLabel: "인증등급",
                },
                {
                    key: "run",
                    grade: measuredGrade,
                    shortLabel: timeName,
                    fullLabel: `운영등급 (${timeName})`,
                },
                {
                    key: "totRun",
                    grade: totalOperatingGrade,
                    shortLabel: "전체",
                    fullLabel: "운영등급(전체)",
                },
            ];

            return indicators
                .filter((ind) => this.gradeToYIndex(ind.grade) >= 0)
                .map((ind) => ({
                    name: ind.fullLabel,
                    coord: [PILL_SLOT_X[ind.key], ind.grade],
                    grade: ind.grade,
                    fullLabel: ind.fullLabel,
                    symbol: "roundRect",
                    symbolSize: PILL_SYMBOL_SIZE,
                    itemStyle: {
                        color: this.pillGradient(ind.key),
                        shadowBlur: 8,
                        shadowColor: "rgba(0, 0, 0, 0.18)",
                        shadowOffsetY: 2,
                        borderColor: "rgba(255, 255, 255, 0.45)",
                        borderWidth: 1,
                    },
                    label: {
                        show: true,
                        formatter: ind.shortLabel,
                        color: "#fff",
                        fontSize: 10,
                        fontWeight: 600,
                    },
                }));
        },
        renderChart() {
            if (!this.data || !this.data.values || !this.data.values.grade) {
                console.warn("[EnergyEfficiencyChart] 데이터가 없습니다.");
                return;
            }

            if (this.chart) {
                this.chart.dispose();
            }

            this.chart = echarts.init(this.$refs.pyramidChart);

            const colorGradient = [
                "#4caf50",
                "#7cb342",
                "#cddc39",
                "#ffeb3b",
                "#ffc107",
                "#ff9800",
                "#ff7043",
                "#ff5722",
                "#f44336",
                "#d32f2f",
            ];

            const measuredGrade = this.data.values.grade.measuredGrade || null;
            const certifiedGrade = this.data.values.grade.certifiedGrade || null;
            const totalOperatingGrade = this.data.values.grade.totalOperatingGrade || null;
            const markPointData = this.buildMarkPointData(measuredGrade, certifiedGrade, totalOperatingGrade);
            const activeYIndexes = this.getActiveGradeIndexes(certifiedGrade, measuredGrade, totalOperatingGrade);
            const trackData = this.yLabels.map(() => GRADE_TRACK_MAX);

            const option = {
                graphic: [],
                tooltip: {
                    trigger: "item",
                    confine: true,
                    backgroundColor: "rgba(255, 255, 255, 0.96)",
                    borderColor: "rgba(0, 0, 0, 0.06)",
                    borderWidth: 1,
                    padding: [10, 14],
                    textStyle: { color: "#37474F", fontSize: 12 },
                    extraCssText: "border-radius:10px;box-shadow:0 8px 24px rgba(0,0,0,0.1);",
                    formatter: (params) => {
                        if (params.componentType === "markPoint" && params.data?.fullLabel) {
                            const { fullLabel, grade } = params.data;
                            return `<div style="font-weight:600;color:#263238;margin-bottom:6px;">${fullLabel}</div>
                                <div style="color:#546E7A;">등급 <b style="color:#1565C0;">${grade}</b></div>`;
                        }
                        if (params.seriesName === "에너지효율 등급") {
                            return `${params.name}`;
                        }
                        return "";
                    },
                },
                legend: {
                    top: 2,
                    left: "center",
                    itemWidth: 8,
                    itemHeight: 8,
                    itemGap: 16,
                    icon: "circle",
                    textStyle: { fontSize: 10, color: "#607D8B" },
                    data: [
                        { name: "인증등급", itemStyle: { color: THEME.cert.main } },
                        { name: `운영등급 (${this.timeName})`, itemStyle: { color: THEME.run.main } },
                        { name: "운영등급(전체)", itemStyle: { color: THEME.totRun.main } },
                    ],
                },
                grid: {
                    top: CHART_PAD_TOP,
                    left: 4,
                    right: 6,
                    bottom: CHART_PAD_BOTTOM,
                    containLabel: false,
                },
                xAxis: {
                    type: "value",
                    min: 0,
                    max: X_AXIS_MAX,
                    show: false,
                    axisLine: { show: false },
                    axisTick: { show: false },
                    splitLine: { show: false },
                },
                yAxis: {
                    type: "category",
                    inverse: true,
                    data: this.yLabels,
                    axisLine: { show: false },
                    axisTick: { show: false },
                    axisLabel: { show: false },
                    splitLine: { show: false },
                },
                series: [
                    {
                        name: "등급 트랙",
                        type: "bar",
                        data: trackData,
                        barWidth: BAR_WIDTH,
                        barGap: "-100%",
                        barCategoryGap: BAR_CATEGORY_GAP,
                        silent: true,
                        z: 1,
                        tooltip: { show: false },
                        itemStyle: {
                            color: "rgba(227, 242, 253, 0.65)",
                            borderRadius: [0, 8, 8, 0],
                            borderColor: "rgba(144, 202, 249, 0.35)",
                            borderWidth: 1,
                        },
                    },
                    {
                        name: "에너지효율 등급",
                        type: "bar",
                        z: 10,
                        barWidth: BAR_WIDTH,
                        barGap: "-100%",
                        barCategoryGap: BAR_CATEGORY_GAP,
                        data: this.buildGradeBarSeriesData(colorGradient, activeYIndexes),
                        label: {
                            show: true,
                            position: "insideLeft",
                            distance: 4,
                            formatter: (params) => `${params.name}`,
                            color: "#fff",
                            fontSize: 9,
                            fontWeight: 600,
                            textShadowColor: "rgba(0, 0, 0, 0.35)",
                            textShadowBlur: 2,
                        },
                        markPoint: {
                            symbolKeepAspect: false,
                            data: markPointData,
                        },
                    },
                ],
            };

            this.chart.setOption(option, true);
            this.$nextTick(() => this.chart.resize());
        },
    },
};
</script>

<style scoped>
.container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
}
.titleContainer {
    flex-shrink: 0;
    margin-bottom: 2px;
    font-size: 18px;
    color: #4d4d4d;
    font-weight: 800;
    display: flex;
    justify-content: center;
    align-items: center;
}
.common-subtitle {
    margin: 0;
    font-size: 11px;
    line-height: 1.2;
}
.pyramidChart {
    flex: 1;
    min-height: 0;
    width: 100%;
    margin-top: -4px;
}
</style>
